/**
 * FrenchMac Global JavaScript
 * Interactions communes et fonctionnalités
 * Compatible : Chrome 60+, Firefox 55+, Safari 11+, Edge 79+, Opera 47+
 */

(function () {
  "use strict";

  // ============================================
  // FEATURE DETECTION
  // ============================================

  var supportsPassive = false;
  try {
    var opts = Object.defineProperty({}, "passive", {
      get: function () {
        supportsPassive = true;
        return true;
      },
    });
    window.addEventListener("testPassive", null, opts);
    window.removeEventListener("testPassive", null, opts);
  } catch (e) {
    supportsPassive = false;
  }

  var passiveOption = supportsPassive ? { passive: true } : false;

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Debounce - Limite l'exécution d'une fonction
   * @param {Function} func - Fonction à exécuter
   * @param {number} wait - Délai en ms
   * @returns {Function}
   */
  function debounce(func, wait) {
    var timeout;
    return function executedFunction() {
      var context = this;
      var args = arguments;
      var later = function () {
        clearTimeout(timeout);
        func.apply(context, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Throttle - Limite la fréquence d'exécution
   * @param {Function} func - Fonction à exécuter
   * @param {number} limit - Intervalle minimum en ms
   * @returns {Function}
   */
  function throttle(func, limit) {
    var inThrottle;
    return function () {
      var context = this;
      var args = arguments;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(function () {
          inThrottle = false;
        }, limit);
      }
    };
  }

  // Smooth scroll for anchor links
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener("click", function (e) {
        const targetId = this.getAttribute("href");
        if (targetId === "#") return;

        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          e.preventDefault();
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    });
  }

  // Mobile menu toggle
  function initMobileMenu() {
    const menuButton = document.querySelector(".frenchmac-header__mobile-menu");
    const mobileNav = document.getElementById("mobile-nav");

    if (!menuButton || !mobileNav) return;

    menuButton.addEventListener("click", function () {
      const isActive = mobileNav.classList.toggle("active");
      this.classList.toggle("active");

      // Prevent body scroll when menu is open
      if (isActive) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }

      // Accessibility
      this.setAttribute("aria-expanded", isActive);
      mobileNav.setAttribute("aria-hidden", !isActive);
    });

    // Close menu when clicking outside
    document.addEventListener("click", function (event) {
      if (
        !menuButton.contains(event.target) &&
        !mobileNav.contains(event.target)
      ) {
        if (mobileNav.classList.contains("active")) {
          mobileNav.classList.remove("active");
          menuButton.classList.remove("active");
          document.body.style.overflow = "";
        }
      }
    });

    // Close menu on escape key
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && mobileNav.classList.contains("active")) {
        mobileNav.classList.remove("active");
        menuButton.classList.remove("active");
        document.body.style.overflow = "";
        menuButton.focus();
      }
    });
  }

  // Sticky header on scroll - optimisé avec requestAnimationFrame
  function initStickyHeader() {
    var header = document.querySelector(".frenchmac-header");
    if (!header) return;

    var lastScroll = 0;
    var headerHeight = header.offsetHeight;
    var ticking = false;

    function updateHeader() {
      var currentScroll =
        window.pageYOffset || document.documentElement.scrollTop;

      if (currentScroll <= 0) {
        header.classList.remove("frenchmac-header--scrolled");
        header.classList.remove("frenchmac-header--hidden");
      } else if (currentScroll > lastScroll && currentScroll > headerHeight) {
        // Scrolling down
        header.classList.add("frenchmac-header--hidden");
      } else if (currentScroll < lastScroll) {
        // Scrolling up
        header.classList.remove("frenchmac-header--hidden");
        header.classList.add("frenchmac-header--scrolled");
      }

      lastScroll = currentScroll;
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }

    // Utiliser passive listener pour de meilleures performances de scroll
    window.addEventListener("scroll", onScroll, passiveOption);
  }

  // Lazy load images
  function initLazyLoad() {
    if ("loading" in HTMLImageElement.prototype) {
      const images = document.querySelectorAll('img[loading="lazy"]');
      images.forEach((img) => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
        }
      });
    } else {
      // Fallback for older browsers
      const script = document.createElement("script");
      script.src = "/assets/lazysizes.min.js";
      document.body.appendChild(script);
    }
  }

  // Animate on scroll - avec vérification de support
  function initScrollAnimation() {
    // Vérifier le support de IntersectionObserver
    if (!("IntersectionObserver" in window)) {
      // Fallback : afficher directement les éléments sans animation
      var cards = document.querySelectorAll(".frenchmac-service-card");
      for (var i = 0; i < cards.length; i++) {
        cards[i].classList.add("animate-fade-in");
      }
      return;
    }

    // Respecter la préférence utilisateur pour les animations réduites
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      var cards = document.querySelectorAll(".frenchmac-service-card");
      for (var i = 0; i < cards.length; i++) {
        cards[i].classList.add("animate-fade-in");
      }
      return;
    }

    var observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -100px 0px",
    };

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-in");
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    var cards = document.querySelectorAll(".frenchmac-service-card");
    for (var i = 0; i < cards.length; i++) {
      observer.observe(cards[i]);
    }
  }

  // Form validation
  function initFormValidation() {
    const forms = document.querySelectorAll("form[data-validate]");

    forms.forEach((form) => {
      form.addEventListener("submit", function (e) {
        const inputs = form.querySelectorAll(
          "input[required], textarea[required], select[required]"
        );
        let isValid = true;

        inputs.forEach((input) => {
          if (!input.value.trim()) {
            isValid = false;
            input.classList.add("error");

            // Show error message
            let errorMsg = input.nextElementSibling;
            if (!errorMsg || !errorMsg.classList.contains("error-message")) {
              errorMsg = document.createElement("span");
              errorMsg.className = "error-message";
              errorMsg.textContent = "Ce champ est requis";
              input.parentNode.insertBefore(errorMsg, input.nextSibling);
            }
          } else {
            input.classList.remove("error");
            const errorMsg = input.nextElementSibling;
            if (errorMsg && errorMsg.classList.contains("error-message")) {
              errorMsg.remove();
            }
          }
        });

        if (!isValid) {
          e.preventDefault();
        }
      });
    });
  }

  // Phone number formatting (French)
  function initPhoneFormatting() {
    const phoneInputs = document.querySelectorAll('input[type="tel"]');

    phoneInputs.forEach((input) => {
      input.addEventListener("input", function (e) {
        var v = e.target.value;
        // ⚠️ Numéro INTERNATIONAL (avec « + ») : on n'y touche JAMAIS — ni reformat ni
        // troncature. (L'ancien code supprimait le « + » puis tronquait à 14 caractères,
        // ce qui mangeait les chiffres d'un +33… → numéros WhatsApp inutilisables.)
        if (v.indexOf("+") !== -1) return;
        var digits = v.replace(/\D/g, "");
        if (!digits) return;
        // Embellissement UNIQUEMENT pour le local français (0X…), SANS jamais tronquer.
        // « 00… » (préfixe international) et autres formats : laissés intacts.
        if (digits.charAt(0) === "0" && digits.charAt(1) !== "0") {
          e.target.value = (digits.match(/.{1,2}/g) || []).join(" ");
        }
      });
    });
  }

  // Close horizontal facet dropdowns when clicking outside or pressing Escape
  // Fix : par défaut, les <details class="facets__disclosure"> ne se referment
  // que si l'on reclique sur leur <summary> (la pilule). On ajoute donc un
  // listener global pour fermer tout filtre ouvert dès qu'on clique en dehors.
  function initFacetDropdownAutoClose() {
    function closeOpenFacets(except) {
      var openFacets = document.querySelectorAll(
        "details.facets__disclosure[open]"
      );
      for (var i = 0; i < openFacets.length; i++) {
        if (except && openFacets[i] === except) continue;
        openFacets[i].removeAttribute("open");
        var summary = openFacets[i].querySelector("summary");
        if (summary) summary.setAttribute("aria-expanded", "false");
      }
    }

    // Click outside : ferme tous les filtres ouverts.
    document.addEventListener("click", function (event) {
      var clickedFacet = event.target.closest(
        "details.facets__disclosure"
      );

      // Si on clique sur une pilule de filtre actif (croix de suppression),
      // on ne touche à rien — le lien fait son travail naturellement.
      if (event.target.closest(".active-facets__button")) return;

      if (!clickedFacet) {
        // Clic en dehors de tout filtre : on ferme tout.
        closeOpenFacets(null);
      } else {
        // Clic à l'intérieur d'un filtre : on ferme les autres
        // pour qu'un seul dropdown reste ouvert à la fois.
        closeOpenFacets(clickedFacet);
      }
    });

    // Echap : ferme également les filtres ouverts.
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" || event.key === "Esc") {
        closeOpenFacets(null);
      }
    });
  }

  // Track button clicks
  function initAnalytics() {
    const ctaButtons = document.querySelectorAll("[data-track]");

    ctaButtons.forEach((button) => {
      button.addEventListener("click", function () {
        const action = this.dataset.track;

        // Send to analytics if available
        if (typeof gtag !== "undefined") {
          gtag("event", "click", {
            event_category: "CTA",
            event_label: action,
          });
        }

        console.log("Tracked:", action);
      });
    });
  }

  // ============================================
  // VIEWPORT & DEVICE DETECTION
  // ============================================

  /**
   * Détecte le type d'appareil et ajuste si nécessaire
   */
  function initViewportDetection() {
    var html = document.documentElement;

    // Détecter les appareils tactiles
    if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
      html.classList.add("touch-device");
    } else {
      html.classList.add("no-touch");
    }

    // Détecter les grands écrans (4K et plus)
    function updateViewportClass() {
      var width = window.innerWidth;

      html.classList.remove(
        "viewport-mobile",
        "viewport-tablet",
        "viewport-desktop",
        "viewport-4k",
        "viewport-8k"
      );

      if (width < 750) {
        html.classList.add("viewport-mobile");
      } else if (width < 991) {
        html.classList.add("viewport-tablet");
      } else if (width < 2560) {
        html.classList.add("viewport-desktop");
      } else if (width < 3840) {
        html.classList.add("viewport-4k");
      } else {
        html.classList.add("viewport-8k");
      }
    }

    updateViewportClass();
    window.addEventListener("resize", debounce(updateViewportClass, 100));

    // Détecter les écrans haute densité (Retina)
    if (window.devicePixelRatio && window.devicePixelRatio >= 2) {
      html.classList.add("retina");
    }
  }

  /**
   * Fix pour la hauteur de viewport sur iOS Safari
   * Gère la barre d'adresse dynamique
   */
  function initViewportHeightFix() {
    function setVH() {
      var vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", vh + "px");
    }

    setVH();
    window.addEventListener("resize", debounce(setVH, 100));
    window.addEventListener("orientationchange", function () {
      setTimeout(setVH, 100);
    });
  }

  // ============================================
  // ERROR HANDLING
  // ============================================

  /**
   * Wrapper pour exécuter les fonctions en toute sécurité
   */
  function safeExecute(fn, name) {
    try {
      fn();
    } catch (error) {
      console.warn("FrenchMac: Error in " + name + ":", error);
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  function init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init);
      return;
    }

    // Exécuter chaque fonction de manière sécurisée
    safeExecute(initViewportDetection, "initViewportDetection");
    safeExecute(initViewportHeightFix, "initViewportHeightFix");
    safeExecute(initSmoothScroll, "initSmoothScroll");
    safeExecute(initMobileMenu, "initMobileMenu");
    safeExecute(initStickyHeader, "initStickyHeader");
    safeExecute(initLazyLoad, "initLazyLoad");
    safeExecute(initScrollAnimation, "initScrollAnimation");
    safeExecute(initFormValidation, "initFormValidation");
    safeExecute(initPhoneFormatting, "initPhoneFormatting");
    safeExecute(initFacetDropdownAutoClose, "initFacetDropdownAutoClose");
    safeExecute(initAnalytics, "initAnalytics");

    // Marquer comme initialisé
    document.documentElement.classList.add("frenchmac-ready");

    if (window.console && window.console.log) {
      console.log("FrenchMac scripts initialized");
    }
  }

  // Start initialization
  init();

  // Expose utility functions globally
  window.FrenchMac = {
    debounce: debounce,
    throttle: throttle,
  };
})();
